import {
    emailSubjects,
    stepNames,
    stepComponentNames,
    formFields,
} from '../constants';
import { IAnswerSelectStep, IStep } from '../types';

export interface ISteps {
    [key: string]: IStep | IAnswerSelectStep;
}

export const steps: ISteps = {
    [stepNames.subscriptionQuestion]: {
        id: 0,
        stepComponentName: stepComponentNames.answerSelect,
        title: 'Heeft je vraag betrekking op het <strong>contactlenzenabonnement</strong>?',
        titleSummary: 'Betrekking op lenzenabonnement',
        options: [
            {
                title: 'Ja',
                sendToDeliveryService: true,
                nextSteps: [stepNames.subjectSelect],
            },
            {
                title: 'Nee',
                sendToDeliveryService: false,
                nextSteps: [stepNames.subjectSelect],
            },
        ],
    },
    [stepNames.subjectSelect]: {
        id: 1,
        stepComponentName: stepComponentNames.subjectSelect,
        titleSummary: 'Onderwerp',
        options: [
            {
                title: 'Ik heb een vraag',
                titleHtml: 'Ik heb een <strong>vraag</strong>',
                nextSteps: [stepNames.purchaseCheck],
                subject: emailSubjects.question,
            },
            {
                title: 'Ik heb een klacht',
                titleHtml: 'Ik heb een <strong>klacht</strong>',
                nextSteps: [stepNames.purchaseCheck],
                subject: emailSubjects.complaint,
            },
            {
                title: 'Ik wil wat wijzigen',
                titleHtml: 'Ik wil wat <strong>wijzigen</strong>',
                nextSteps: [stepNames.purchaseCheck],
                subject: emailSubjects.change,
            },
            {
                title: 'Ik heb een compliment',
                titleHtml: 'Ik heb een <strong>compliment</strong>',
                nextSteps: [stepNames.storeComplimentCheck],
                subject: emailSubjects.compliment,
            },
            {
                title: 'Inzage oogmeetgegevens',
                titleHtml: 'Inzage <strong>oogmeetgegevens</strong>',
                subject: emailSubjects.viewPrescriptionInfo,
                nextSteps: [stepNames.privacyMessage],
            },
            {
                title: 'Inzage persoonsgegevens (AVG)',
                titleHtml: 'Inzage <strong> persoonsgegevens (AVG)</strong>',
                nextSteps: [stepNames.purchaseReceiptCheck],
                subject: emailSubjects.viewPersonalInfo,
            },
            {
                title: 'Verwijderen persoonsgegevens (AVG)',
                titleHtml:
                    'Verwijderen <strong>persoonsgegevens (AVG)</strong>',
                nextSteps: [stepNames.purchaseReceiptCheck],
                subject: emailSubjects.deletePersonalInfo,
            },
            {
                title: 'Overdragen persoonsgegevens (AVG)',
                titleHtml: 'Overdragen <strong>persoonsgegevens (AVG)</strong>',
                nextSteps: [stepNames.purchaseReceiptCheck],
                subject: emailSubjects.movePersonalInfo,
            },
        ],
    },
    [stepNames.purchaseCheck]: {
        id: 2,
        stepComponentName: stepComponentNames.answerSelect,
        titleSummary: 'Betreft aankoop',
        options: [
            {
                title: 'Ja',
                nextSteps: [stepNames.purchaseReceiptCheck],
            },
            {
                title: 'Nee',
                storeRequired: false,
                nextSteps: [
                    stepNames.customerContact,
                    stepNames.storeSelectOptional,
                    stepNames.customerMessage,
                ],
            },
        ],
    },
    [stepNames.storeComplimentCheck]: {
        id: 3,
        stepComponentName: stepComponentNames.answerSelect,
        title: 'Is jouw compliment bestemd voor een <strong>specifieke winkel</strong>?',
        titleSummary: 'Compliment voor winkel',
        options: [
            {
                title: 'Ja',
                nextSteps: [stepNames.storeSelect, stepNames.customerMessage],
            },
            {
                title: 'Nee',
                nextSteps: [stepNames.customerMessage],
            },
        ],
    },
    [stepNames.privacyMessage]: {
        id: 4,
        stepComponentName: stepComponentNames.privacyMessage,
        titleSummary: '',
        options: [],
    },
    [stepNames.purchaseReceiptCheck]: {
        id: 5,
        stepComponentName: stepComponentNames.answerSelect,
        title: 'Ben je in het bezit van een <strong>actuele aankoopbon</strong>?',
        titleSummary: 'Aankoopbon',
        options: [
            {
                title: 'Ja',
                nextSteps: [stepNames.receiptInfoCheck],
            },
            {
                title: 'Nee',
                nextSteps: [
                    stepNames.customerContact,
                    stepNames.customerAddress,
                    stepNames.storeSelectOptional,
                    stepNames.customerMessage,
                ],
            },
        ],
    },
    [stepNames.receiptInfoCheck]: {
        id: 6,
        stepComponentName: stepComponentNames.answerSelect,
        title: 'Zijn de <strong>persoonsgegevens</strong> op deze bon nog correct?',
        titleSummary: 'Correcte persoonsgegevens',
        options: [
            {
                title: 'Ja',
                nextSteps: [stepNames.purchaseTypeCheck],
            },
            {
                title: 'Nee',
                nextSteps: [
                    stepNames.customerContact,
                    stepNames.customerAddress,
                    stepNames.storeSelectOptional,
                    stepNames.customerMessage,
                ],
            },
        ],
    },
    [stepNames.purchaseTypeCheck]: {
        id: 7,
        stepComponentName: stepComponentNames.answerSelect,
        title: 'Wat voor <strong>soort aankoop</strong> was het?',
        titleSummary: 'Soort aankoop',
        options: [
            {
                title: 'Online',
                nextSteps: [
                    stepNames.orderNumberCheck,
                    stepNames.storeSelect,
                    stepNames.customerMessage,
                ],
                onlineOrder: true,
            },
            {
                title: 'In de winkel',
                nextSteps: [
                    stepNames.orderNumberCheck,
                    stepNames.storeSelect,
                    stepNames.customerMessage,
                ],
                onlineOrder: false,
            },
        ],
    },
    [stepNames.storeSelect]: {
        id: 8,
        stepComponentName: stepComponentNames.storeSelect,
        titleSummary: 'Filiaal',
        formFields: [formFields.store],
        options: [
            {
                title: 'Gebruik deze winkel',
            },
            {
                title: 'Ik weet het niet',
                skipValidation: true,
                secondaryButton: true,
                nextSteps: [
                    stepNames.customerContact,
                    stepNames.customerAddress,
                    stepNames.customerMessage,
                ],
            },
        ],
    },
    [stepNames.storeSelectOptional]: {
        id: 9,
        stepComponentName: stepComponentNames.storeSelectOptional,
        titleSummary: 'Winkel',
        formFields: [formFields.store],
        options: [
            {
                title: 'Gebruik deze winkel',
            },
            {
                title: 'Ik ben nog geen klant',
                skipValidation: true,
                secondaryButton: true,
            },
        ],
    },
    [stepNames.orderNumberCheck]: {
        id: 10,
        titleSummary: 'Order/ -factuurnummer ',
        formFields: [formFields.orderNumber],
        stepComponentName: stepComponentNames.orderNumberForm,
        options: [
            {
                title: 'Gebruik dit nummer',
            },
            {
                title: 'Ik weet het niet',
                secondaryButton: true,
                skipValidation: true,
                nextSteps: [
                    stepNames.customerContact,
                    stepNames.customerAddress,
                    stepNames.storeSelectOptional,
                    stepNames.customerMessage,
                ],
            },
        ],
    },
    [stepNames.customerContact]: {
        id: 11,
        stepComponentName: stepComponentNames.nameForm,
        titleSummary: 'Contactgegevens',
        formFields: [
            formFields.salutation,
            formFields.firstName,
            formFields.lastNamePrefix,
            formFields.lastName,
            formFields.phoneNumber,
            formFields.customerEmail,
        ],
        options: [
            {
                title: 'Volgende',
            },
        ],
    },
    [stepNames.customerAddress]: {
        id: 12,
        stepComponentName: stepComponentNames.addressForm,
        titleSummary: 'Adresgegevens',
        formFields: [
            formFields.street,
            formFields.houseNumber,
            formFields.houseNumberAddition,
            formFields.zipcode,
            formFields.city,
        ],
        options: [
            {
                title: 'Volgende',
            },
        ],
    },
    [stepNames.customerMessage]: {
        id: 13,
        stepComponentName: stepComponentNames.messageForm,
        titleSummary: '',
        formFields: [
            formFields.firstName,
            formFields.lastNamePrefix,
            formFields.lastName,
            formFields.customerEmail,
            formFields.message,
        ],
        options: [],
    },
};
